import { useSetAtom } from 'jotai';
import type { RootStateOrAny } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';

import type { CornerstoneAnnotationFindingTypes } from '../../@types/global';
import type {
  GetPatientLatestImagesResponse,
  LatestImage,
} from '../../@types/latestImages';
import { addImageToState } from '../uploader/UploaderActions';

import AnnotatorConstants from './annotator.constants';
import { clearPatientImages } from './AnnotatorActions';
import { latestImagesAtom } from './findingSidebar/atoms';
import type { BoundingBoxFinding, ImageObject, UserAnnotation } from './types';

export const createUserAnnotation = (
  finding: BoundingBoxFinding,
  image: LatestImage,
  type: CornerstoneAnnotationFindingTypes,
): UserAnnotation => ({
  id: finding.id,
  annotationId: finding.id,
  imageId: image.imageId,
  source: 'userAnnotations',
  sourceImageId: image.imageId,
  active: false,
  color: AnnotatorConstants.COLORS.default,
  spaceBetweenDashes: AnnotatorConstants.DASHLINES.LOOSE,
  type,
  invalidated: true,
  visible: true,
  handles: {
    start: {
      x: finding.xMin,
      y: finding.yMin,
    },
    end: {
      x: finding.xMax,
      y: finding.yMax,
    },
    textBox: {},
  },
  validation: finding.userValidation || 'Agree',
  xMin: finding.xMin,
  xMax: finding.xMax,
  yMin: finding.yMin,
  yMax: finding.yMax,
});

const addValidationToFinding = (finding: any) => ({
  ...finding,
  ...(finding.userValidation && {
    validation: finding.userValidation,
  }),
});

const createValidatedFindings = (img: any) => {
  const { models } = img;

  return {
    carieFindings: models.caries?.findings?.map(addValidationToFinding) || [],
    prlFindings:
      models.periapical_radiolucency?.findings?.map(addValidationToFinding) ||
      [],
    rblFindings:
      models.radiographic_boneloss?.findings?.map((rbl: any) => ({
        ...rbl,
        sides: rbl.sides.map(addValidationToFinding),
      })) || [],
    restorativeAdjacentLucencyFindings:
      models.restorative_adjacent_lucency?.findings?.map(
        addValidationToFinding,
      ) || [],
  };
};

export const useCreateInitialFindings = () => {
  const setLatestImages = useSetAtom(latestImagesAtom);

  const user = useSelector((state: RootStateOrAny) => state?.login?.user);
  const dispatch = useDispatch();

  const createImageObjects = (
    latestImagesData: GetPatientLatestImagesResponse,
  ) => {
    return latestImagesData.analyzedImages.map((img) => {
      const initialUserAnnotations: any = [];
      const {
        carieFindings,
        prlFindings,
        rblFindings,
        restorativeAdjacentLucencyFindings,
      } = createValidatedFindings(img);

      // Latest Images V2 user annotations add
      img.userAnnotations?.forEach((userAnnotation) => {
        initialUserAnnotations.push(
          createUserAnnotation(
            userAnnotation,
            img,
            userAnnotation.label as CornerstoneAnnotationFindingTypes,
          ),
        );
      });

      const image = {
        ...img,
        userAnnotations: initialUserAnnotations,
        models: {
          ...img.models,
          caries: { ...img.models.caries, findings: carieFindings },
          periapical_radiolucency: {
            ...img.models.periapical_radiolucency,
            findings: prlFindings,
          },
          radiographic_boneloss: {
            ...img.models.radiographic_boneloss,
            findings: rblFindings,
          },
          restorative_adjacent_lucency: {
            ...img.models.restorative_adjacent_lucency,
            findings: restorativeAdjacentLucencyFindings,
          },
        },
        estimatedVisitId: latestImagesData.estimatedVisitId,
        // Instead of changing that in multiple places just set the prop here
        objectId: img.imageId,
        analysisId: latestImagesData.analyses?.find(
          (analysisImage) => analysisImage.mongoImageId === img.imageId,
        )?.imageId,
      };

      dispatch(addImageToState(image, user));
      return image;
    });
  };

  const createInitialFindings = (
    latestImagesData: GetPatientLatestImagesResponse,
    patientId: string,
  ) => {
    dispatch(clearPatientImages(patientId));
    const images = createImageObjects(latestImagesData);
    setLatestImages(images);
  };

  return { createInitialFindings, createImageObjects };
};

/**
 * Determines if we should use Series view instead of FMX view
 *
 * @param images - Array of images to check
 * @returns True if should use Series view (≤4 FMX images)
 */
export const shouldUseSeriesView = (
  images: (ImageObject | LatestImage)[],
  enableSeriesView: boolean = false,
): boolean => {
  if (!images || !enableSeriesView) return false;

  // Only count images that have an fmxIndex (FMX images)
  const fmxImages = images.filter((image) => {
    return (
      image.imageId && image.fmxIndex !== null && image.fmxIndex !== undefined
    );
  });

  return fmxImages.length <= 4;
};
